import React from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { IconColor, IconName } from "@/shared/ui/Icon";
import { Card } from "@/shared/ui/Card";
import IconBadge from "@/shared/ui/IconBadge";
import { cn } from "@/shared/utils/shadcn";
import { HighightedItemCard } from "@/shared/ui/HighightedItemCard";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/shared/ui/HoverCard";
import { upperFirst } from "lodash";

export interface HighlightedItemProps {
  label: string;
  description: string;
  id: number;
}

interface HighlightedItemsListProps {
  title: string;
  icon?: IconName;
  iconColor?: IconColor;
  items: HighlightedItemProps[];
}

const gradientsMap = {
  green:
    "bg-linear-to-b from-green-100 via-green-100/50 to-transparent bg-size-[100%_40%] bg-no-repeat",
  orange:
    "bg-linear-to-b from-orange-100 via-orange-100/50 to-transparent bg-size-[100%_40%] bg-no-repeat",
  primary:
    "bg-linear-to-b from-blue-100 via-blue-100/50 to-transparent bg-size-[100%_40%] bg-no-repeat",
};

function HighlightedItemsList(props: HighlightedItemsListProps) {
  const { title, icon, iconColor, items } = props;
  return (
    <Column className="flex-1 min-w-[200px] gap-4">
      <Row className="gap-4 justify-center items-center mb-4">
        {icon && <IconBadge hasOpacity={false} icon={icon} color={iconColor} />}
        <h4>{title}</h4>
      </Row>
      {!!items &&
        items?.map((item) => (
          <HoverCard openDelay={100} closeDelay={100} key={item.id}>
            <HoverCardTrigger asChild>
              <Card className="p-4 cursor-pointer" key={item.id}>
                <p> {item.label}</p>
              </Card>
            </HoverCardTrigger>
            <HoverCardContent side="top" className="p-0">
              <HighightedItemCard key={item.id} {...item} />
            </HoverCardContent>
          </HoverCard>
        ))}
    </Column>
  );
}

function HighlightedItemsListDetailed(props: HighlightedItemsListProps) {
  const { title, icon, iconColor, items } = props;
  return (
    <Column
      className={cn(
        "flex-1 min-w-[200px] gap-4 p-8 print:px-0 break-inside-avoid",
        gradientsMap[iconColor as keyof typeof gradientsMap] ||
          gradientsMap.primary,
        "print:bg-none"
      )}
    >
      <Row className="gap-4 items-center mb-4">
        {icon && <IconBadge hasOpacity={false} icon={icon} color={iconColor} />}
        <h4>{upperFirst(title)}</h4>
      </Row>
      <Row className="gap-4">
        {!!items &&
          items?.map((item) => <HighightedItemCard key={item.id} {...item} />)}
      </Row>
    </Column>
  );
}

export { HighlightedItemsList, HighlightedItemsListDetailed };
